import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUp,
  ClipboardList,
  Compass,
  Loader2,
  MessageCircle,
  PanelRightClose,
  Sparkles,
  Trash2,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useSession } from "@/stores/session";
import { useWorkspace, type WorkspaceView } from "@/stores/workspace";
import { api } from "@/lib/api";
import type { AdapterName } from "@/lib/adapters";
import { cn } from "@/lib/cn";

type Suggestion = { label: string; icon: typeof Compass; view?: WorkspaceView };

interface PageContext {
  greeting: string;
  suggestions: Suggestion[];
}

const DEFAULT_CONTEXT: PageContext = {
  greeting: "quick GMAT prep assistant — ask me anything.",
  suggestions: [
    { label: "What should I study today?", icon: Compass },
    { label: "What's a good score target for M7?", icon: Sparkles },
    { label: "Build me a mock exam", icon: ClipboardList, view: "exam" },
    { label: "How do I pace Data Insights?", icon: Compass },
  ],
};

const PAGE_CONTEXT: Partial<Record<WorkspaceView, PageContext>> = {
  topics: {
    greeting: "need a quick answer? I'm the chatbot — for full lessons, pick a topic and open the Tutor.",
    suggestions: [
      { label: "Which topics have the best ROI?", icon: Compass },
      { label: "What's the difference between DS and PS?", icon: Sparkles },
    ],
  },
  tutor: {
    greeting: "I'm the quick-help chatbot. For step-by-step teaching, you're already in the Tutor tab.",
    suggestions: [
      { label: "What's a good study plan for 6 weeks?", icon: Compass },
      { label: "Explain the assumption negation test briefly", icon: Sparkles },
    ],
  },
  exam: {
    greeting: "ready to build or debrief a mock?",
    suggestions: [
      { label: "How should I pace a full GMAT Focus mock?", icon: Compass },
      { label: "What's a good score target for Wharton?", icon: Sparkles },
    ],
  },
  practice: {
    greeting: "let's sharpen a weak area.",
    suggestions: [
      { label: "Which topic should I drill first?", icon: Compass },
      { label: "Why do I keep picking trap answers on DS?", icon: Sparkles },
    ],
  },
  score: {
    greeting: "let's talk about your trajectory.",
    suggestions: [
      { label: "What would push me past 700?", icon: Sparkles },
      { label: "How accurate is this prediction?", icon: Compass },
    ],
  },
};

/** Chatbot routing — fast answers, not live tutoring. */
function pickChatbotAdapter(text: string): AdapterName {
  const t = text.toLowerCase();
  if (t.match(/plan|schedule|study plan|weeks|roadmap|how long/)) return "lesson-planner";
  if (t.match(/score|predict|700|680|admit|percentile/)) return "score-predictor";
  if (t.match(/weak|gap|struggling|worst at|improve/)) return "weakness-detector";
  if (t.match(/mock|generate|practice question/)) return "socratic-tutor";
  return "socratic-tutor";
}

export function InsightsPanel({ onClose }: { onClose: () => void }) {
  const view = useWorkspace((s) => s.view);
  const setView = useWorkspace((s) => s.setView);
  const pendingChatbotPrompt = useWorkspace((s) => s.pendingChatbotPrompt);
  const consumeChatbotPrompt = useWorkspace((s) => s.consumeChatbotPrompt);
  const pageCtx = useMemo(() => PAGE_CONTEXT[view] ?? DEFAULT_CONTEXT, [view]);
  const chat = useSession((s) => s.chat);
  const appendTurn = useSession((s) => s.appendTurn);
  const clearChat = useSession((s) => s.clearChat);
  const user = useSession((s) => s.user);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sendRef = useRef<(text: string, gotoView?: WorkspaceView) => void>(() => {});

  useEffect(() => {
    const el = scrollRef.current?.querySelector("[data-radix-scroll-area-viewport]") as HTMLElement | null;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.length, busy]);

  const send = async (text: string, gotoView?: WorkspaceView) => {
    const q = text.trim();
    if (!q || busy) return;
    if (gotoView) {
      setView(gotoView);
      return;
    }
    appendTurn({ role: "user", content: q });
    setInput("");
    setBusy(true);
    try {
      const history = useSession.getState().chat.slice(-8).map((t) => ({
        role: t.role as "user" | "assistant",
        content: t.content,
      }));
      const res = await api.tutorChat({
        adapter: pickChatbotAdapter(q),
        persona: "assistant",
        messages: [...history, { role: "user", content: q }],
        max_tokens: 420,
        temperature: 0.55,
      });
      appendTurn({ role: "assistant", content: res.content });
    } catch {
      appendTurn({
        role: "assistant",
        content: "I couldn't reach the server just now. Try again in a moment.",
      });
    } finally {
      setBusy(false);
    }
  };

  sendRef.current = send;

  // Prompt forwarded from Topics "Ask Maya" bar or elsewhere.
  useEffect(() => {
    if (!pendingChatbotPrompt) return;
    const p = consumeChatbotPrompt();
    if (p) void sendRef.current(p);
  }, [pendingChatbotPrompt, consumeChatbotPrompt]);

  const firstName = user?.name?.split(" ")[0] ?? "there";

  return (
    <div className="flex h-full flex-col bg-card/40 backdrop-blur-sm">
      <header className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="grid size-7 place-items-center rounded-full border border-border/60 bg-background">
            <MessageCircle className="size-3.5 text-muted-foreground" />
          </div>
          <div>
            <div className="text-sm font-medium leading-none">Ask Maya</div>
            <div className="mt-0.5 text-[10px] text-muted-foreground">Quick help · chatbot</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {chat.length > 0 && (
            <button
              type="button"
              onClick={clearChat}
              title="Clear conversation"
              className="grid size-8 place-items-center rounded-lg text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            >
              <Trash2 className="size-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            title="Hide panel"
            className="grid size-8 place-items-center rounded-lg text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
          >
            <PanelRightClose className="size-4" />
          </button>
        </div>
      </header>

      <ScrollArea ref={scrollRef} className="flex-1">
        <div className="space-y-3 p-4">
          {chat.length === 0 ? (
            <div className="space-y-4 pt-2">
              <p className="font-serif text-lg leading-snug">
                Hi {firstName} — {pageCtx.greeting}
              </p>
              <p className="text-sm text-muted-foreground">
                Fast answers on plans, scores, and strategy. For a full lesson, use{" "}
                <strong className="font-medium text-foreground">Topics → Start tutoring</strong>.
              </p>
              <div className="space-y-2">
                {pageCtx.suggestions.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => send(s.label, s.view)}
                    className="flex w-full items-center gap-2.5 rounded-xl border border-border/60 bg-background/60 px-3 py-2.5 text-left text-sm transition-colors hover:bg-secondary/60"
                  >
                    <s.icon className="size-4 shrink-0 text-accent" />
                    <span>{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {chat.map((turn) => (
                <motion.div
                  key={turn.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "flex",
                    turn.role === "user" ? "justify-end" : "justify-start",
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                      turn.role === "user"
                        ? "bg-foreground text-background"
                        : "border border-border/60 bg-background/70",
                    )}
                  >
                    {turn.content}
                  </div>
                </motion.div>
              ))}
              {busy && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" /> Maya is typing…
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {chat.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto px-3 pt-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {pageCtx.suggestions.slice(0, 3).map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => send(s.label, s.view)}
              className="flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <s.icon className="size-3" />
              {s.label}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="border-t border-border/60 p-3"
      >
        <div className="flex items-end gap-2 rounded-2xl border border-border/60 bg-background/70 p-1.5 pl-3 focus-within:ring-2 focus-within:ring-ring/40">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            rows={1}
            placeholder="Ask anything…"
            className="max-h-32 flex-1 resize-none bg-transparent py-1.5 text-sm outline-none placeholder:text-muted-foreground"
          />
          <button
            type="submit"
            disabled={!input.trim() || busy}
            className="grid size-8 shrink-0 place-items-center rounded-full bg-foreground text-background transition-opacity disabled:opacity-40"
          >
            <ArrowUp className="size-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
